import { fetchAuthSession, signOut } from "aws-amplify/auth";
import axios from "axios";
import { router } from "../main";

const instance = axios.create({
  baseURL: import.meta.env.VITE_PUBLIC_LOCAL_BASE_URL,
});

// Helper to check if an error is a network/timeout error
const isNetworkError = (error: unknown): boolean => {
  // Check for axios network errors (no response received)
  if (axios.isAxiosError(error) && !error.response) {
    return true;
  }
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    const name = error.name.toLowerCase();
    return (
      message.includes("network") ||
      message.includes("timeout") ||
      message.includes("fetch") ||
      message.includes("econnrefused") ||
      message.includes("enotfound") ||
      message.includes("failed to fetch") ||
      name.includes("networkerror") ||
      name.includes("timeouterror") ||
      name === "typeerror"
    );
  }
  return false;
};

instance.interceptors.request.use(async (config: any) => {
  const { accessToken, idToken }: any = (await fetchAuthSession()).tokens ?? {};
  if (idToken && config.headers) {
    config.headers = {
      Authorization: `Bearer ${idToken}`,
      "ngrok-skip-browser-warning": "abc",
    };
  }
  return config;
});

// Response interceptor to handle authentication errors with token refresh
instance.interceptors.response.use(
  (response) => {
    // Return successful responses as-is
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Don't redirect on network errors - let the UI handle them gracefully
    if (isNetworkError(error)) {
      console.warn(
        "Network error detected, not redirecting to login:",
        error.message,
      );
      return Promise.reject(error);
    }

    // Handle authentication errors (401 Unauthorized, 403 Forbidden)
    if (
      error.response &&
      (error.response.status === 401 || error.response.status === 403) &&
      !originalRequest._retry
    ) {
      console.warn(
        "Authentication error detected, attempting token refresh:",
        error.response.status,
      );

      originalRequest._retry = true;

      try {
        // Attempt to refresh the session
        const { accessToken, idToken }: any =
          (await fetchAuthSession({ forceRefresh: true })).tokens ?? {};

        if (idToken) {
          // Update the original request with the new token
          originalRequest.headers = {
            ...originalRequest.headers,
            Authorization: `Bearer ${idToken}`,
            "ngrok-skip-browser-warning": "abc",
          };

          // Retry the original request with the new token
          return instance(originalRequest);
        } else {
          throw new Error("No valid token after refresh");
        }
      } catch (refreshError) {
        // If refresh failed due to network error, don't sign out - just reject the request
        if (isNetworkError(refreshError)) {
          console.warn(
            "Token refresh failed due to network error, not signing out",
          );
          return Promise.reject(error);
        }

        console.error("Token refresh failed:", refreshError);

        // Only sign out if token refresh fails and it's a genuine auth error
        // Hub will emit signedOut event which updates useAuthStatus
        try {
          await signOut();
          // Redirect to login page
          router.navigate({ to: "/login" });
        } catch (signOutError) {
          console.error("Error during sign out:", signOutError);
          // Force redirect even if signOut fails
          router.navigate({ to: "/login" });
        }
      }
    }

    // Re-throw the error for other status codes or if it's not an authentication error
    return Promise.reject(error);
  },
);

export default instance;
